import { useEffect, useRef } from "react"
import { Platform, Pressable, View, TextInput as TI } from "react-native"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"

import { ar } from "@src/i18n/ar"
import { trpc } from "@src/utils/trpc"

import { ModalWrapper, Text, TextInput, MCIcon } from "./core"
import { updateLowStockThresholdInput } from "@white-labels/input"

interface UpdateLowStockThresholdModalProps {
  onClose: () => void
  productId: number
  storagePlaceId: number
  currentThreshold: number | null
}

export function UpdateLowStockThresholdModal({ onClose, productId, storagePlaceId, currentThreshold }: UpdateLowStockThresholdModalProps) {
  const utils = trpc.useUtils()
  const updateThreshold = trpc.inventory.updateLowStockThreshold.useMutation()
  const thresholdRef = useRef<TI | null>(null)

  const { control, handleSubmit, formState, setValue } = useForm({
    resolver: zodResolver(updateLowStockThresholdInput),
    defaultValues: {
      productId,
      storagePlaceId,
      lowStockThreshold: currentThreshold,
    },
  })

  const loading = updateThreshold.isPending
  const disabled = !formState.isValid || loading

  const onSubmit = handleSubmit((data) => {
    updateThreshold.mutate(data, {
      onSuccess: () => {
        utils.products.get.invalidate({ id: productId })
        utils.storagePlaces.listProducts.invalidate()
        onClose()
      },
      onError: (error) => {
        alert(error.message || ar.somethingWentWrong)
      },
    })
  })

  // Auto-focus on open
  useEffect(() => {
    requestAnimationFrame(() => thresholdRef.current?.focus())
  }, [])

  // Close on Escape (web)
  useEffect(() => {
    if (Platform.OS === "web") {
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose()
      }
      window.addEventListener("keydown", handler)
      return () => window.removeEventListener("keydown", handler)
    }
  }, [onClose])

  return (
    <ModalWrapper onClose={onClose} className="rounded-none shadow-none p-4 max-w-lg">
      {/* Header */}
      <View className="flex-row items-center justify-between mb-4">
        <Text className="text-lg font-bold text-gray-900 tracking-tight">{ar.setLowStockThreshold}</Text>
        <Pressable onPress={onClose} className="p-2">
          <MCIcon name="close" size={22} color="#6B7280" />
        </Pressable>
      </View>

      {/* Current Threshold Display */}
      {currentThreshold != null && (
        <View className="bg-yellow-50 p-3 mb-4 border border-yellow-200">
          <Text className="text-sm text-yellow-700 font-bold">{ar.lowStockThreshold}</Text>
          <Text className="text-xl font-bold text-yellow-900">{currentThreshold}</Text>
        </View>
      )}

      {/* Threshold Input */}
      <Text className="text-base font-bold text-gray-900 mb-2">{ar.lowStockThreshold}</Text>
      <Controller
        control={control}
        name="lowStockThreshold"
        render={({ field: { onChange, value } }) => (
          <TextInput
            ref={thresholdRef}
            placeholder="0"
            placeholderTextColor="#9CA3AF"
            keyboardType="number-pad"
            textAlign="right"
            selectTextOnFocus
            value={value?.toString() ?? ""}
            onChangeText={(text) => {
              const num = Number(text)
              if (Number.isNaN(num)) return

              onChange(num)
            }}
          />
        )}
      />

      {/* Info Note */}
      <View className="bg-blue-50 p-3 mb-4 border border-blue-100">
        <Text className="text-sm text-blue-700 leading-relaxed text-right">{ar.clearLowStockThresholdInfo}</Text>
      </View>

      {/* Action Buttons */}
      <View className="flex-row gap-3">
        <Pressable
          className="flex-1 h-8 mt-2 items-center justify-center active:opacity-90 disabled:opacity-50 bg-red-100 border border-red-200"
          onPress={() => {
            setValue("lowStockThreshold", null)
            onSubmit()
          }}
          disabled={loading}
        >
          <Text className="text-red-700 font-bold">{ar.clearLowStockThreshold}</Text>
        </Pressable>

        <Pressable
          className={`flex-1 h-8 mt-2 items-center justify-center active:opacity-90 disabled:opacity-50 ${loading ? "bg-gray-400" : "bg-blue-600"}`}
          onPress={onSubmit}
          disabled={disabled}
        >
          <Text className="text-white font-bold tracking-wide">{loading ? ar.processing : ar.confirm}</Text>
        </Pressable>
      </View>
    </ModalWrapper>
  )
}
